import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { Box, Grid, Link, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, gridClasses } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { TbCalendarTime, TbSend } from 'react-icons/tb';
import { Link as RouterLink } from 'react-router-dom';
import {
  ISubmissionsAPIResponse,
  amplitude,
  ICandidate,
  getStageStatusByCurrentUser,
  getJobById,
  IJob,
  ISubmissionStageStatusSetting,
  ICandidateSubmissionSearchFilterVM,
  updateCandidateSubmission,
  IUserDTO,
} from 'wisestep-api';
import {
  WsColors,
  WsIconText,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
  timeSince,
  WsIconNoUserGeneric,
  formateDateInDateMonthYear,
  formateDateForNewSubmission,
  WsButton,
  WsChip,
  WsChipSize,
  WsChipVariants,
} from 'wisestep-base';

import NoDataFoundCard from '../../../../components/noDataFoundCard/NoDataFoundCard';
import { useUserContext } from '../../../../context';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext/WorkspaceContext';
import { ClintPageBreadCrumb } from '../../../../features/clientDetails/helper/clientDetailsHelper';
import ErrorLogger from '../../../../providers/ErrorLogger';
import DefaultStageStatusPipeline from '../../../../utils/DefaultStageStatusPipeline';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import EmailPopUp from '../../../candidateLayout/components/EmailPopUp';
import SubmitOptions from '../../../conversationWidget/components/SubmitOptions';
import { SubmissionType } from '../../../conversationWidget/types/SubmissionTypes';
import { useSubmissionStageStatus } from '../../../crmSettings/hooks/useSubmissionStageStatus';
import {
  ISubmissionsChildProps,
  buildNotes,
  generateSubmissionBackwardUrl,
  getSortModel,
} from '../../utils/Helper';
import CandidateInfoDialog from '../widgets/CandidateInfoDialog';
import StageStatusDialogTool from '../widgets/StageStatusDialogTool';
import WsDataGridPagination from '../widgets/WsDataGridPagination';

import CustomHeader from './CustomHeader';

export interface ISubmissionsTableProps extends ISubmissionsChildProps {
  getUpdatedFiltersFromTable: (data) => void;
  submissionsData: ISubmissionsAPIResponse;
  refreshPendingCount: () => void;
  refetch: () => void;
  updateSubmissionData: (updatedData: ICandidate[]) => void;
  updateSearchQuery: (updatedSearchQuery: ICandidateSubmissionSearchFilterVM) => void;
}

type SortModel = {
  field: string;
  sort: 'asc' | 'desc' | undefined;
}[];
const Table = ({
  searchQuery,
  selectedCandidate,
  unSelect = false,
  selectRows = false,
  updateRow,
  getUpdatedFiltersFromTable,
  submissionsData,
  refreshPendingCount,
  refetch,
  updateSearchQuery,
}: ISubmissionsTableProps) => {
  const [sortModel, setSortModel] = useState<SortModel>([
    searchQuery.sort.length
      ? getSortModel(searchQuery.sort[0])
      : {
          field: 'createdOn',
          sort: 'desc',
        },
  ]);

  const [selectionModel, setSelectionModel] = useState<any[]>([]);
  const [selectedC, setSelectedC] = useState<ICandidate[]>();
  const [currentUserStageStatus, setCurrentUserStageStatus] = useState<any>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<IJob | null>(null);
  const [sendCandidate, setSendCandidate] = useState<ICandidate | null>(null);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm();
  const workspace = useWorkspaceContext();
  const { submissionStageData } = useSubmissionStageStatus();
  const isAdmin = workspace.owns;
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [submissionEmailBody, setSubmissionEmailBody] = useState<string>('');

  const loggedInUserName =
    loggedInUser?.firstName?.trim() || loggedInUser?.lastName?.trim() || loggedInUser?.email || '';

  useEffect(() => {
    bulkRowUpdate(updateRow ?? []);
    selectedCandidate && selectedCandidate([]);
    setSelectionModel([]);
    setSelectedC([]);
  }, [updateRow]);

  const candidateArray = useMemo(() => {
    const candidateList: string[] = Array.from(
      new Set(submissionsData?.content?.map((submission) => submission.candidateId ?? ''))
    );
    return candidateList;
  }, [submissionsData]);

  const bulkRowUpdate = (data: ICandidate[]) => {
    data.forEach((newObj) => {
      const index = submissionsData.content.findIndex((obj) => obj.id === newObj.id);
      if (index !== -1) {
        submissionsData.content[index] = newObj;
      }
    });
  };

  const setUrl = (candidateId) =>
    `/candidate-layout/submissions?id=${candidateId}&array=${candidateArray}&backwardpathArray=${getEncodedQueryParams(
      generateSubmissionBackwardUrl()
    )}
    `;
  // const updateUI = (data: ICandidate) => {
  //   // const updatedStatuses = submissionsData.content.map((candidate) => {
  //   //   if (candidate.id === data.id) {
  //   //     return data;
  //   //   }
  //   //   return candidate;
  //   // });
  //   refetch();
  //   // updateSubmissionData(updatedStatuses);
  // };

  useEffect(() => {
    const fetchStageStatus = async () => {
      try {
        const response = await getStageStatusByCurrentUser();
        if (response?.data?.stageStatusPipeline)
          setCurrentUserStageStatus(
            JSON.parse(response?.data?.stageStatusPipeline ?? DefaultStageStatusPipeline)
          );
        else setCurrentUserStageStatus(DefaultStageStatusPipeline);
      } catch (error) {
        console.log(error);
      }
    };
    fetchStageStatus();
  }, []);

  const handleSort = (field: string, nextSortDirection: 'asc' | 'desc' | undefined) => {
    const newSortModel = nextSortDirection ? [{ field, sort: nextSortDirection }] : [];
    updateSearchQuery({
      ...searchQuery,
      sort: nextSortDirection ? [`${field},${nextSortDirection}`] : [],
    });
    setSortModel(newSortModel);
  };

  const handleSubmitClick = (event: React.MouseEvent<HTMLButtonElement>, candidate: ICandidate) => {
    setAnchorEl(event.currentTarget);
    setSendCandidate(candidate);
    fetchJob(candidate?.jobId ?? '');
  };

  const fetchJob = async (jobId: string) => {
    const respose = await getJobById(jobId);
    if (respose?.data) setSelectedJob(respose?.data);
  };

  const submitAssignment = async (
    rec: any,
    type: SubmissionType,
    comment: string,
    emailBody?: string
  ) => {
    const submissionSettings: Partial<ISubmissionStageStatusSetting> =
      submissionStageData.find((item) => item.submissionType === type) || {};

    const payload: ICandidate = {
      id: sendCandidate?.id ?? '',
      candidateId: sendCandidate?.candidateId ?? '',
      assignTo: rec?.email ?? '',
      assignToUserName: rec?.name ?? '',
      assignBy: sendCandidate?.assignTo ?? '',
      assignByUserName: sendCandidate?.assignToUserName ?? '',
      previousAssignmentId: sendCandidate?.assignmentId ?? '',
      submissionType: String(type),
      applicationStage: submissionSettings.applicationStage ?? 'CV Submitted',
      applicationState: submissionSettings.applicationState ?? 'Feedback Pending',
      candidateNotes: buildNotes(comment, loggedInUserName) ?? [],
      assignmentUpdate: true,
      jobId: sendCandidate?.jobId ?? '',
      submissionEmailBody: emailBody ?? '',
    };

    try {
      const response = await updateCandidateSubmission([payload]);
      if (response?.data?.[0]) {
        setNotification(success('Submission assignment is successful'));
        refreshPendingCount();
        if (type === SubmissionType.INTERNAL || type === SubmissionType.INTERNALLY_REJECT) {
          refetch();
        } else {
          setSubmissionEmailBody(response?.data?.[0]?.submissionEmailBody ?? '');
          setOpenEmail(true);
        }
      } else {
        setNotification(warning('Submission assignment API failed'));
      }
    } catch (error) {
      setNotification(warning('Error occurred during submission assignment'));
    } finally {
      handlePopoverClose();
    }
  };

  const handleInternalSubmit = (rec: any, comment: string, emailBody: string) =>
    submitAssignment(rec, SubmissionType.INTERNAL, comment, emailBody);

  const handleExternalSubmit = (rec: boolean, emailBody: string) => {
    if (rec) submitAssignment(null, SubmissionType.EXTERNAL, '', emailBody);
    else handlePopoverClose();
  };

  const handleSubmissionRejection = (comment: string) =>
    submitAssignment(null, SubmissionType.INTERNALLY_REJECT, comment);

  const handlePopoverClose = () => setAnchorEl(null);

  const emailDrawerClose = () => {
    setOpenEmail(false);
    refetch();
  };

  const myQueueFilter = searchQuery?.filters?.find((filter) => filter.filterName === 'MY_QUEUE');
  const isMyQueuePendingTab = myQueueFilter?.values?.[0] === 'PENDING';
  const isMyQueueAssignedTab = myQueueFilter?.values?.[0] === 'ASSIGNED';

  const columnsName: GridColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: '',
        hide: true,
      },
      {
        field: 'candidateName',
        headerName: 'Candidate Name',
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          const candidate = submissionsData?.content[params.row.rowIndex];
          return (
            <Grid container xs={12}>
              <Grid item xs={9} sx={{ wordBreak: 'break-word' }}>
                <Box pr={0.5} pt={'8px'}>
                  <Link
                    component={RouterLink}
                    to={setUrl(candidate.candidateId)}
                    sx={{ textDecoration: 'none' }}
                  >
                    <WsTypography
                      variant={WsTypoVariants.BODY1}
                      color={WsTypoColors.PRIMARY600}
                      bold
                      cursor="pointer"
                      onClick={() => {
                        amplitude('CandidateView_Redirect_Submission', {
                          clickedOn: 'Redirect Submission',
                          pageName: 'candidate-layout',
                          pageUrl: window.location.href,
                        });
                      }}
                    >
                      {candidate?.name ?? 'NA'}
                    </WsTypography>
                  </Link>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <CandidateInfoDialog candidateInfo={candidate} candidateIds={candidateArray} />
              </Grid>
            </Grid>
          );
        },
      },
      {
        field: 'jobTitle',
        headerName: 'Job Title',
        flex: 0.75,
        sortable: false,
        renderCell: (params) => {
          const candidate = submissionsData?.content[params.row.rowIndex];
          return (
            <Link href={`/jobs-crm/${candidate?.jobId}/false/1/pipeline`} underline="none">
              <WsTypography
                variant={WsTypoVariants.BODY2}
                color={WsTypoColors.PRIMARY600}
                cursor={'pointer'}
                bold
              >
                {candidate.mappedJobTitle}
              </WsTypography>
            </Link>
          );
        },
      },
      {
        field: 'clientName',
        headerName: 'Client Name',
        flex: 0.75,
        sortable: false,
        renderCell: (params) => {
          const candidate = submissionsData?.content[params.row.rowIndex];
          return (
            <>
              {candidate?.companyName && !/.*xxxxxx.*/.test(candidate.companyName) ? (
                <Link
                  href={`/client-details?tabIndex=0&isChild=true&name=${
                    candidate?.companyName
                  }&jobCode=${candidate?.jobCode}&breadCrumbList=${getEncodedQueryParams(
                    ClintPageBreadCrumb['CLIENTS']
                  )}`}
                  underline="none"
                >
                  <WsTypography
                    variant={WsTypoVariants.CAPTION}
                    color={WsTypoColors.PRIMARY}
                    bold={true}
                    cursor="pointer"
                  >
                    {candidate.companyName ?? 'Not Available'}
                  </WsTypography>
                </Link>
              ) : (
                <WsTypography
                  variant={WsTypoVariants.CAPTION}
                  color={WsTypoColors.PRIMARY}
                  bold={true}
                >
                  {candidate.companyName ?? 'Not Available'}
                </WsTypography>
              )}
            </>
          );
        },
      },
      {
        field: 'createdOn',
        headerName: 'Submitted On',
        flex: 1,
        sortable: true,
        hideSortIcons: true,
        renderHeader: (params) => (
          <CustomHeader params={params} sortModel={sortModel} handleSort={handleSort} />
        ),
        renderCell: (params) => (
          <Tooltip title={formateDateForNewSubmission(params.value ?? '')} placement="bottom">
            <span>{timeSince(params.value ?? '')}</span>
          </Tooltip>
        ),
      },
      {
        field: 'submittedBy',
        headerName: 'Submitted By',
        sortable: false,
        flex: 0.75,
        renderCell: (params) => {
          const candidate = submissionsData?.content[params.row.rowIndex];
          return (
            <Link
              href={`/recruiter/profile?email=${candidate.modifiedBy}&isLink=true&goBackUrl=/all-submissions&isUserAnalytics=true`}
              underline="none"
            >
              <WsTypography
                variant={WsTypoVariants.BODY2}
                color={candidate.modifiedBy ? WsTypoColors.PRIMARY600 : WsTypoColors.BLACK}
                cursor={candidate.modifiedBy ? 'pointer' : undefined}
              >
                {candidate.recruiterName ?? 'NA'}
              </WsTypography>
            </Link>
          );
        },
      },
      {
        field: 'assignedTo',
        headerName: 'Assigned To',
        sortable: false,
        flex: 0.75,
        renderCell: (params) => {
          const candidate = submissionsData?.content[params.row.rowIndex];
          return (
            <Link
              href={`/recruiter/profile?email=${candidate.assignTo}&isLink=true&goBackUrl=/all-submissions&isUserAnalytics=true`}
              underline="none"
            >
              <WsTypography
                variant={WsTypoVariants.BODY2}
                color={candidate.assignTo ? WsTypoColors.PRIMARY600 : WsTypoColors.BLACK}
                cursor={candidate.assignTo ? 'pointer' : undefined}
              >
                {candidate.assignToUserName ?? candidate.assignTo ?? 'NA'}
              </WsTypography>
            </Link>
          );
        },
      },
      {
        field: 'stageStatus',
        headerName: 'Stage Status',
        sortable: false,
        flex: 2,
        renderCell: (params) => {
          const candidate = submissionsData?.content[params.row.rowIndex];
          return (
            <Box>
              <Grid container>
                <WsChip
                  value={candidate?.applicationStage ?? ''}
                  size={WsChipSize.MEDIUM}
                  variant={WsChipVariants.STAGE}
                  borderColor={WsColors.TheBlues400}
                />
                <WsChip
                  value={candidate?.applicationState ?? ''}
                  size={WsChipSize.MEDIUM}
                  variant={WsChipVariants.STATUS}
                  borderColor={WsColors.YellowBorder}
                />

                <div>
                  <StageStatusDialogTool
                    candidateInfo={candidate}
                    userStageStatus={currentUserStageStatus}
                    updateUI={refetch}
                  />
                </div>
              </Grid>
              <Grid container p={1} gap={1}>
                <WsIconText
                  icon={
                    <AccessTimeOutlinedIcon
                      sx={{ color: WsColors.TertiaryShades800 }}
                      fontSize="small"
                    />
                  }
                  text={
                    (candidate?.modifiedOn &&
                      (new Date().getTime() - new Date(candidate?.modifiedOn).getTime() <
                      24 * 60 * 60 * 1000
                        ? timeSince(candidate?.modifiedOn)
                        : formateDateInDateMonthYear(candidate?.modifiedOn))) ??
                    'Invalid Date'
                  }
                  textCursor="text"
                  textColor={WsTypoColors.QUINARY}
                  popOverText={`Last Updated ${formateDateInDateMonthYear(
                    candidate?.modifiedOn ?? ''
                  )}`}
                />
                {candidate?.stageDate && (
                  <WsIconText
                    icon={
                      <NotificationsNoneOutlinedIcon
                        sx={{ color: WsColors.TertiaryShades800 }}
                        fontSize="small"
                      />
                    }
                    text={formateDateInDateMonthYear(candidate?.stageDate)}
                    textCursor="text"
                    textColor={WsTypoColors.QUINARY}
                    popOverText="Stage Date"
                  />
                )}
                {candidate?.joiningDate && (
                  <WsIconText
                    icon={<TbCalendarTime size={18} color={WsColors.Green500} />}
                    text={formateDateInDateMonthYear(
                      new Date(candidate?.joiningDate.split('T')[0]).toString()
                    )}
                    textCursor="text"
                    textColor={WsTypoColors.QUINARY}
                    popOverText="Joining Date"
                  />
                )}
              </Grid>
            </Box>
          );
        },
      },
      ...(isMyQueuePendingTab || (isAdmin && !isMyQueueAssignedTab)
        ? [
            {
              field: 'send',
              headerName: '',
              flex: 0.5,
              sortable: false,
              renderCell: (params) => {
                const candidate = submissionsData?.content?.[params.row.rowIndex];
                return (
                  <WsButton
                    type="button"
                    variant="outlined"
                    onClick={(event) =>
                      handleSubmitClick(event as React.MouseEvent<HTMLButtonElement>, candidate)
                    }
                    disabled={
                      candidate?.submissionType === SubmissionType.EXTERNAL ||
                      candidate?.submissionType === SubmissionType.INTERNALLY_REJECT
                    }
                  >
                    <TbSend size={20} />
                  </WsButton>
                );
              },
            },
          ]
        : []),
    ],
    [submissionsData?.content, sortModel]
  );
  const tableData: Record<string, unknown>[] = useMemo(
    () =>
      submissionsData?.content?.map((submission, index) => ({
        id: submission.id,
        candidateName: submission.name ?? 'NA',
        jobTitle: submission.mappedJobTitle,
        clientName: submission.companyName,
        createdOn: submission.createdOn ?? '',
        submittedBy: submission?.recruiterName ?? 'Not Available',
        stageStatus: submission.applicationStage,
        rowIndex: index,
      })),
    [submissionsData?.content]
  );

  const handleSelectionChange = (newSelection) => {
    setSelectedC(
      newSelection.map((candidateId) => {
        return submissionsData?.content.find((obj) => obj.id === candidateId);
      })
    );
    setSelectionModel(newSelection);
  };

  useEffect(() => {
    selectedCandidate1(selectedC);
  }, [selectedC]);

  const selectedCandidate1 = (value) => selectedCandidate && selectedCandidate(value);

  useEffect(() => {
    unSelect && selectedCandidate && selectedCandidate([]);
    unSelect && setSelectionModel([]);
    unSelect && setSelectedC([]);
  }, [unSelect]);

  useEffect(() => {
    const candidateId = submissionsData?.content?.map((content) => content.id ?? '');
    selectRows && selectedCandidate && selectedCandidate(submissionsData?.content);
    selectRows && candidateId && setSelectionModel(candidateId ?? []);
    selectRows && setSelectedC(submissionsData?.content);
  }, [selectRows]);
  const handlePageChange = (event, newPage) =>
    getUpdatedFiltersFromTable({ ...searchQuery, page: newPage - 1 });

  const handlePageSizeChange = (event) =>
    getUpdatedFiltersFromTable({ ...searchQuery, size: event.target.value, page: 0 });

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <Grid
        justifyContent="space-between"
        display="flex"
        bgcolor="white"
        alignItems="center"
        border="1px  solid #E0E0E0"
        borderTop={0}
        p={0}
      >
        <Box pl={2.5}>
          <WsTypography color={WsTypoColors.BLACK} variant={WsTypoVariants.TITLE} bold>
            All ({submissionsData.totalElements})
          </WsTypography>
        </Box>
        <WsDataGridPagination
          count={Math.ceil(submissionsData?.totalElements / searchQuery.size)}
          page={searchQuery.page + 1}
          onPageChange={handlePageChange}
          pageSize={searchQuery.size ?? 15}
          onPageSizeChange={handlePageSizeChange}
        />
      </Grid>
      {isEmpty(tableData) ? (
        <NoDataFoundCard
          text="No submissions to show for applied filter(s)"
          icon={<WsIconNoUserGeneric />}
          subElement={<WsTypography>No submissions</WsTypography>}
        />
      ) : (
        <Grid item xs={12} bgcolor={WsColors.White}>
          <DataGrid
            autoHeight
            checkboxSelection
            hideFooterSelectedRowCount
            columns={columnsName}
            rows={tableData}
            onPageChange={(page) => getUpdatedFiltersFromTable({ ...searchQuery, page })}
            page={searchQuery.page}
            rowCount={submissionsData?.totalElements}
            pageSize={searchQuery.size}
            paginationMode="server"
            rowsPerPageOptions={[25, 50, 75, 100]}
            onPageSizeChange={handlePageSizeChange}
            sortingMode="client"
            getRowHeight={() => 'auto'}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              '& .MuiDataGrid-root': {
                borderRadius: 0,
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'transparent',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              borderTopRightRadius: '0',
              borderTopLeftRadius: '0',
              borderTop: '0',
            }}
            onSelectionModelChange={(newSelection) => handleSelectionChange(newSelection)}
            selectionModel={selectionModel}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model as SortModel)}
          />
        </Grid>
      )}
      {
        <FormProvider {...methods}>
          <SubmitOptions
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            onInternalSubmit={handleInternalSubmit}
            onExternalSubmit={handleExternalSubmit}
            getSubmissionPayload={() => sendCandidate}
            onSubmissionRejection={handleSubmissionRejection}
            job={selectedJob}
            submissionMailBody={sendCandidate?.submissionEmailBody ?? null}
          />
        </FormProvider>
      }
      {openEmail && sendCandidate && selectedJob && submissionEmailBody && (
        <EmailPopUp
          candidate={sendCandidate}
          job={selectedJob}
          openEmail={openEmail}
          onClose={emailDrawerClose}
          mailBody={submissionEmailBody}
          fromSubmissions={true}
        />
      )}
    </ErrorBoundary>
  );
};
export default Table;
